/**
 * artifact-segments.js — split an assistant reply into an ordered list of
 * "text" and "artifact" segments so the chat renderer can HIDE raw fenced
 * code blocks (```html …``` etc) behind a clean preview card instead of
 * streaming them line-by-line into the transcript.
 *
 * Non-technical users get overwhelmed by raw markup; the goal is that a
 * generated website surfaces as a soft "Building your website…" pulse that
 * settles into a friendly "Website draft ready — Preview" card, with the
 * actual HTML kept out of the message body.
 *
 * Streaming rules:
 *   • A CLOSED fenced block that `parseArtifacts` accepts becomes a
 *     `{ kind: "artifact", artifact }` segment.
 *   • An OPEN fenced block (fence-opener seen, closing ``` not yet arrived)
 *     becomes a `{ kind: "artifact-building", lang }` segment and everything
 *     after the opener is dropped from the visible text.
 */
import { parseArtifacts } from "./artifact-parser";

const OPEN_FENCE_RE = /```([a-zA-Z0-9_+.\-]*)(?:\n|$)/;

export function splitByArtifacts(text, { streaming = false } = {}) {
  if (!text || typeof text !== "string") {
    return text ? [{ kind: "text", text }] : [];
  }
  const artifacts = parseArtifacts(text);
  const segments = [];
  let cursor = 0;

  artifacts.forEach((art, i) => {
    if (art.start > cursor) {
      segments.push({ kind: "text", text: text.slice(cursor, art.start) });
    }
    segments.push({ kind: "artifact", artifact: art, id: `art-${i}` });
    cursor = art.end;
  });

  let tail = text.slice(cursor);
  // Detect an OPEN fence in the tail so we can hide the half-written code
  // from the reader in both cases:
  // (a) `streaming=true` → show the shimmering "Building…" progress card.
  // (b) `streaming=false` (an interrupted stream that was persisted with
  //     no closing fence) → show an "interrupted" state instead of leaking
  //     the raw ` ```html <!DOCTYPE… ` opener into the prose. Callers
  //     distinguish via the `streaming` flag on the returned segment.
  if (tail) {
    const openFence = tail.match(OPEN_FENCE_RE);
    // Only classify as building/interrupted when the fence is genuinely
    // UN-closed. A short block that parseArtifacts skipped (too few
    // chars / lines) but that HAS a closing ``` shouldn't false-positive
    // here — let it flow through as normal markdown.
    if (openFence) {
      const after = tail.slice(openFence.index + openFence[0].length);
      const hasCloser = /```/.test(after);
      if (!hasCloser) {
        const before = tail.slice(0, openFence.index);
        const lang = (openFence[1] || "").trim().toLowerCase();
        if (before) segments.push({ kind: "text", text: before });
        segments.push({
          kind: streaming ? "artifact-building" : "artifact-interrupted",
          lang,
          id: streaming ? "art-building" : "art-interrupted",
        });
        return segments;
      }
    }
  }
  if (tail) segments.push({ kind: "text", text: tail });
  return segments;
}

/** True if the block should be surfaced as a friendly preview card
 *  (a full website) vs. a compact code snippet chip (a shell command). */
export function isPreviewableArtifact(art) {
  return art && (art.kind === "html" || art.kind === "svg");
}
